import { apiFetch, v1Path } from './api';
import { isWorkItemType, type WorkItemType } from './work-types';

export const RELATION_KINDS = ['caused_by', 'fixed_by', 'blocked_by', 'relates_to'] as const;
export type RelationKind = (typeof RELATION_KINDS)[number];

export type RelationEndState = {
  id: string;
  name: string;
  color: string;
  group: string;
};

export type RelationAssignee = {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
};

export type RelationEnd = {
  id: string;
  key: string;
  title: string;
  type: WorkItemType;
  project_id: string;
  project_identifier: string;
  priority: string;
  archived_at: string | null;
  state: RelationEndState;
  assignees: RelationAssignee[];
};

export type RelationDto = {
  id: string;
  kind: RelationKind;
  from: RelationEnd;
  to: RelationEnd;
  created_at: string;
};

function isRelationEndState(value: unknown): value is RelationEndState {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    typeof row.name === 'string' &&
    typeof row.color === 'string' &&
    typeof row.group === 'string'
  );
}

function isRelationAssignee(value: unknown): value is RelationAssignee {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.user_id === 'string' &&
    (row.first_name === null || typeof row.first_name === 'string') &&
    (row.last_name === null || typeof row.last_name === 'string') &&
    (row.email === null || typeof row.email === 'string') &&
    (row.avatar_url === null || typeof row.avatar_url === 'string')
  );
}

export function isRelationEnd(value: unknown): value is RelationEnd {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    typeof row.key === 'string' &&
    typeof row.title === 'string' &&
    typeof row.type === 'string' &&
    isWorkItemType(row.type) &&
    typeof row.project_id === 'string' &&
    typeof row.project_identifier === 'string' &&
    typeof row.priority === 'string' &&
    (row.archived_at === null || typeof row.archived_at === 'string') &&
    isRelationEndState(row.state) &&
    Array.isArray(row.assignees) &&
    row.assignees.every(isRelationAssignee)
  );
}

export function relationAssigneeName(person: RelationAssignee): string {
  const name = [person.first_name, person.last_name]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .trim();
  if (name) return name;
  if (typeof person.email === 'string' && person.email.length > 0) {
    const local = person.email.split('@')[0];
    return local && local.length > 0 ? local : person.email;
  }
  return person.user_id;
}

function isRelationKind(value: unknown): value is RelationKind {
  return typeof value === 'string' && (RELATION_KINDS as readonly string[]).includes(value);
}

function isRelationDto(value: unknown): value is RelationDto {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    isRelationKind(row.kind) &&
    isRelationEnd(row.from) &&
    isRelationEnd(row.to)
  );
}

export async function listRelations(projectId: string, workItemId: string): Promise<RelationDto[]> {
  const res = await apiFetch<unknown>(
    v1Path(`/projects/${projectId}/work-items/${workItemId}/relations`),
  );
  if (!res || typeof res !== 'object' || !Array.isArray((res as { items?: unknown }).items)) {
    throw new Error('Malformed relation list.');
  }
  const items = (res as { items: unknown[] }).items;
  if (!items.every(isRelationDto)) {
    throw new Error('Malformed relation list entry.');
  }
  return items;
}

export async function createRelation(
  projectId: string,
  workItemId: string,
  kind: RelationKind,
  toKey: string,
): Promise<RelationDto> {
  const key = toKey.trim();
  if (!key) {
    throw new Error('Work item key is required.');
  }
  const dto = await apiFetch<unknown>(
    v1Path(`/projects/${projectId}/work-items/${workItemId}/relations`),
    { method: 'POST', body: JSON.stringify({ kind, to_key: key }) },
  );
  if (!isRelationDto(dto)) {
    throw new Error('Malformed relation response.');
  }
  return dto;
}

export async function deleteRelation(relationId: string): Promise<void> {
  await apiFetch(v1Path(`/relations/${relationId}`), { method: 'DELETE' });
}

export function relationKindLabel(kind: RelationKind): string {
  if (kind === 'caused_by') return 'Caused by';
  if (kind === 'fixed_by') return 'Fixed by';
  if (kind === 'blocked_by') return 'Blocked by';
  return 'Related';
}

export function defaultRelationKind(type: string): RelationKind {
  if (type === 'incident') return 'caused_by';
  if (type === 'problem') return 'fixed_by';
  return 'relates_to';
}
